import React from 'react';
import { Badge } from '@shadcn/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@shadcn/components/ui/tooltip';

/**
 * Status colors mapping for meta statuses
 */
const STATUS_COLORS = {
  NEW: {
    bg: 'bg-green-500',
    text: 'text-white',
    hover: 'hover:bg-green-600',
    color: '#4CAF50'
  },
  IN_PROGRESS: {
    bg: 'bg-blue-500',
    text: 'text-white',
    hover: 'hover:bg-blue-600',
    color: '#2196F3'
  },
  WAITING: {
    bg: 'bg-orange-500',
    text: 'text-white',
    hover: 'hover:bg-orange-600',
    color: '#FF9800'
  },
  DONE: {
    bg: 'bg-gray-500',
    text: 'text-white',
    hover: 'hover:bg-gray-600',
    color: '#9E9E9E'
  }
};

/**
 * StatusBadge Component
 * Displays both meta status and original status with proper color coding
 * 
 * @param {Object} props
 * @param {'NEW'|'IN_PROGRESS'|'WAITING'|'DONE'} props.metaStatus - The meta status
 * @param {string} props.originalStatus - The original status from the source system
 * @param {boolean} props.showOriginal - Whether to show original status (default: true)
 * @param {'horizontal'|'vertical'} props.layout - Layout direction (default: 'horizontal')
 * @param {string} props.className - Additional CSS classes
 */
const StatusBadge = ({ 
  metaStatus, 
  originalStatus, 
  showOriginal = true, 
  layout = 'horizontal',
  className = '' 
}) => {
  const statusConfig = STATUS_COLORS[metaStatus] || STATUS_COLORS.NEW;
  
  const isVertical = layout === 'vertical';
  
  return (
    <TooltipProvider>
      <div className={`status-badge-container ${isVertical ? 'flex flex-col gap-1' : 'flex items-center gap-2'} ${className}`}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              className={`
                ${statusConfig.bg} 
                ${statusConfig.text} 
                ${statusConfig.hover} 
                font-medium 
                px-2 
                py-1 
                text-xs 
                rounded-md 
                cursor-help
                transition-colors
                duration-200
              `}
            >
              {metaStatus.replace('_', ' ')}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-sm">
              <div className="font-medium">Meta Status: {metaStatus}</div>
              {originalStatus && (
                <div className="text-gray-600">Original: {originalStatus}</div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
        
        {showOriginal && originalStatus && (
          <span 
            className={`
              original-status-text 
              text-xs 
              text-gray-600 
              ${isVertical ? 'text-center' : ''}
              truncate
              max-w-[120px]
            `}
            title={originalStatus}
          >
            {originalStatus}
          </span>
        )}
      </div>
    </TooltipProvider>
  );
};

/**
 * StatusBadgeSimple - A simpler version without tooltip for performance
 */
export const StatusBadgeSimple = ({ metaStatus, className = '' }) => {
  const statusConfig = STATUS_COLORS[metaStatus] || STATUS_COLORS.NEW;
  
  return (
    <Badge 
      className={`
        ${statusConfig.bg} 
        ${statusConfig.text} 
        font-medium 
        px-2 
        py-1 
        text-xs 
        rounded-md
        ${className}
      `}
    >
      {metaStatus.replace('_', ' ')}
    </Badge>
  );
};

/**
 * StatusDot - A minimal dot indicator for compact displays
 */
export const StatusDot = ({ metaStatus, size = 'sm', className = '' }) => {
  const statusConfig = STATUS_COLORS[metaStatus] || STATUS_COLORS.NEW;
  
  const sizeClasses = {
    xs: 'w-2 h-2',
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  
  return (
    <div 
      className={`
        ${sizeClasses[size]} 
        rounded-full 
        ${statusConfig.bg}
        ${className}
      `}
      title={`Status: ${metaStatus}`}
    />
  );
};

/**
 * Get status color for custom styling
 */
export const getStatusColor = (metaStatus) => {
  return STATUS_COLORS[metaStatus]?.color || STATUS_COLORS.NEW.color;
};

/**
 * Get all available status options
 */
export const getStatusOptions = () => {
  return Object.keys(STATUS_COLORS).map(status => ({
    value: status,
    label: status.replace('_', ' '),
    color: STATUS_COLORS[status].color
  }));
};

/**
 * StatusIndicator - A comprehensive status display with multiple variants
 */
export const StatusIndicator = ({
  metaStatus,
  originalStatus,
  variant = 'badge',
  size = 'md',
  showTooltip = true,
  showOriginal = true,
  className = ''
}) => {
  const statusConfig = STATUS_COLORS[metaStatus] || STATUS_COLORS.NEW;

  const sizeClasses = {
    xs: { badge: 'px-1 py-0.5 text-xs', dot: 'w-2 h-2', text: 'text-xs' },
    sm: { badge: 'px-2 py-1 text-xs', dot: 'w-3 h-3', text: 'text-sm' },
    md: { badge: 'px-2 py-1 text-sm', dot: 'w-4 h-4', text: 'text-sm' },
    lg: { badge: 'px-3 py-1.5 text-sm', dot: 'w-5 h-5', text: 'text-base' }
  };

  const sizeConfig = sizeClasses[size];

  const content = (
    <div className={`flex items-center gap-2 ${className}`}>
      {variant === 'dot' && (
        <div
          className={`${sizeConfig.dot} rounded-full ${statusConfig.bg}`}
          title={showTooltip ? `${metaStatus}: ${originalStatus}` : undefined}
        />
      )}

      {variant === 'badge' && (
        <span className={`
          ${statusConfig.bg}
          ${statusConfig.text}
          ${statusConfig.hover}
          font-medium
          ${sizeConfig.badge}
          rounded-md
          transition-colors
          duration-200
        `}>
          {metaStatus.replace('_', ' ')}
        </span>
      )}

      {variant === 'text' && (
        <span className={`font-medium ${statusConfig.color} ${sizeConfig.text}`}>
          {metaStatus.replace('_', ' ')}
        </span>
      )}

      {showOriginal && originalStatus && variant !== 'dot' && (
        <span className={`text-gray-500 ${sizeConfig.text}`}>
          ({originalStatus})
        </span>
      )}
    </div>
  );

  if (showTooltip && variant !== 'dot') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {content}
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-sm">
              <div className="font-medium">Meta Status: {metaStatus}</div>
              {originalStatus && (
                <div className="text-gray-600">Original: {originalStatus}</div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
};

/**
 * StatusTimeline - Shows status progression over time
 */
export const StatusTimeline = ({
  statuses = [],
  currentStatus,
  className = ''
}) => {
  const statusOrder = ['NEW', 'IN_PROGRESS', 'WAITING', 'DONE'];
  const currentIndex = statusOrder.indexOf(currentStatus);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {statusOrder.map((status, index) => {
        const isActive = index <= currentIndex;
        const isCurrent = status === currentStatus;
        const statusConfig = STATUS_COLORS[status];

        return (
          <div key={status} className="flex items-center">
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium
              ${isActive
                ? `${statusConfig.bg} text-white`
                : 'bg-gray-200 text-gray-500'
              }
              ${isCurrent ? 'ring-2 ring-offset-2 ring-blue-500' : ''}
            `}>
              {index + 1}
            </div>

            {index < statusOrder.length - 1 && (
              <div className={`
                w-8 h-0.5 mx-1
                ${isActive ? 'bg-blue-500' : 'bg-gray-200'}
              `} />
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * StatusCard - A card layout for status display
 */
export const StatusCard = ({
  metaStatus,
  originalStatus,
  title,
  description,
  timestamp,
  className = ''
}) => {
  const statusConfig = STATUS_COLORS[metaStatus] || STATUS_COLORS.NEW;

  return (
    <div className={`
      p-4 rounded-lg border-l-4 bg-white shadow-sm
      ${className}
    `} style={{ borderLeftColor: statusConfig.color }}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge
              metaStatus={metaStatus}
              showOriginal={false}
            />
            {title && (
              <h3 className="font-medium text-gray-900">{title}</h3>
            )}
          </div>

          {description && (
            <p className="text-sm text-gray-600 mb-2">{description}</p>
          )}

          {originalStatus && (
            <p className="text-xs text-gray-500">
              Original status: {originalStatus}
            </p>
          )}
        </div>

        {timestamp && (
          <time className="text-xs text-gray-500">
            {new Date(timestamp).toLocaleString()}
          </time>
        )}
      </div>
    </div>
  );
};

export { StatusBadge as default };
export { StatusBadge };
